"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { FaqSettings, FaqStory } from "./faqs";
import { useOpenWithFocus } from "./use-open-with-focus";

type FaqContextValue = {
  open: boolean;
  // Where the widget was opened from (e.g. "desktop-dock"), for analytics.
  source: string | null;
  setOpen: (open: boolean, source?: string) => void;
  faqs: FaqStory[];
  settings: FaqSettings | null;
  // Lets the Storyblok preview push live editor changes into the drawer.
  setSettings: (next: FaqSettings | null) => void;
  // Replace or insert a single FAQ, so the preview can update one
  // question on the topic page live.
  upsertFaq: (faq: FaqStory) => void;
};

const FaqContext = createContext<FaqContextValue | null>(null);

export function FaqProvider({
  children,
  faqs,
  settings,
}: {
  children: React.ReactNode;
  faqs: FaqStory[];
  settings: FaqSettings | null;
}) {
  const [open, setOpenInner] = useOpenWithFocus(false);
  const [source, setSource] = useState<string | null>(null);
  const setOpen = useCallback(
    (next: boolean, src?: string) => {
      if (next) setSource(src ?? null);
      setOpenInner(next);
    },
    [setOpenInner],
  );
  const [liveSettings, setLiveSettings] = useState<FaqSettings | null>(settings);
  const [liveFaqs, setLiveFaqs] = useState<FaqStory[]>(faqs);
  // Keep state in sync when the server props change (e.g. router.refresh).
  useEffect(() => {
    setLiveSettings(settings);
  }, [settings]);
  useEffect(() => {
    setLiveFaqs(faqs);
  }, [faqs]);
  const upsertFaq = useCallback((faq: FaqStory) => {
    setLiveFaqs((prev) => {
      const i = prev.findIndex((f) => f.uuid === faq.uuid);
      if (i === -1) return [...prev, faq];
      const next = prev.slice();
      next[i] = faq;
      return next;
    });
  }, []);
  const value = useMemo(
    () => ({
      open,
      source,
      setOpen,
      faqs: liveFaqs,
      settings: liveSettings,
      setSettings: setLiveSettings,
      upsertFaq,
    }),
    [open, source, setOpen, liveFaqs, liveSettings, upsertFaq],
  );
  return <FaqContext.Provider value={value}>{children}</FaqContext.Provider>;
}

export function useFaqDrawer() {
  const ctx = useContext(FaqContext);
  if (!ctx) {
    throw new Error("useFaqDrawer must be used inside FaqProvider");
  }
  return ctx;
}
